import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';

const PropertiesContext = createContext(null);

export const useProperties = () => {
  const context = useContext(PropertiesContext);
  if (!context) throw new Error('useProperties must be used within PropertiesProvider');
  return context;
};

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('adminToken');
const authHeaders = () => ({ 'Authorization': `Bearer ${getToken()}` });

export const PropertiesProvider = ({ children }) => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const intervalRef = useRef(null);

  const fetchProperties = useCallback(async (showToast = true) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/properties?limit=1000`, {
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setProperties(data.data || []);
      setSyncStatus(data.syncStatus || null);
      setLastFetched(new Date());
      if (showToast) toast.success(`Loaded ${data.pagination?.total || 0} properties`);
    } catch (err) {
      console.error('Error fetching properties:', err);
      setError(err.message);
      if (showToast) toast.error('Failed to load properties');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPropertyUsers = useCallback(async (propertyId) => {
    const res = await fetch(`${API}/api/properties/${propertyId}/users`, {
      headers: authHeaders(),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  }, []);

  // Full property detail incl. nested CRM fields (otaInfo/paymentInfo/checklistInfo)
  // which the list endpoint strips for performance.
  const fetchPropertyDetail = useCallback(async (propertyId) => {
    const res = await fetch(`${API}/api/properties/${propertyId}`, {
      headers: authHeaders(),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    return json.data || json;
  }, []);

  const toggleUserStatus = useCallback(async (userId, newStatus) => {
    const res = await fetch(`${API}/api/users/${userId}/status`, {
      method: 'PATCH',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: newStatus }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  }, []);

  const updatePropertyStatus = useCallback(async (propertyId, newStatus) => {
    const res = await fetch(`${API}/api/properties/${propertyId}/status`, {
      method: 'PATCH',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: newStatus }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  }, []);

  const resetUserPassword = useCallback(async (userId, body) => {
    const res = await fetch(`${API}/api/users/${userId}/reset-password`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
    return data;
  }, []);

  const notifyPasswordReset = useCallback(async (mobile, ownerName) => {
    const res = await fetch(`${API}/api/whatsapp/notify-password-reset`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ mobile, owner_name: ownerName }),
    });
    return await res.json().catch(() => ({ success: false, error: 'Request failed' }));
  }, []);

  const updatePropertyMapsLink = useCallback(async (propertyId, googleMapsLink) => {
    const res = await fetch(`${API}/api/properties/${propertyId}/maps-link`, {
      method: 'PATCH',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ googleMapsLink }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  }, []);

  const updateBookingVisibility = useCallback(async (propertyId, bookingVisibilityFrom) => {
    const res = await fetch(`${API}/api/properties/${propertyId}/booking-visibility`, {
      method: 'PATCH',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ bookingVisibilityFrom }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `HTTP ${res.status}`);
    }
    return await res.json();
  }, []);

  const bulkUpdateBookingVisibility = useCallback(async (bookingVisibilityFrom, scope = 'all', propertyIds = []) => {
    const res = await fetch(`${API}/api/properties/booking-visibility/bulk`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ bookingVisibilityFrom, scope, propertyIds }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `HTTP ${res.status}`);
    }
    return await res.json();
  }, []);

  const addUser = useCallback(async (propertyId, userData) => {    const res = await fetch(`${API}/api/properties/${propertyId}/users`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(userData),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `HTTP ${res.status}`);
    }
    return await res.json();
  }, []);

  const deleteUser = useCallback(async (userId) => {
    const res = await fetch(`${API}/api/users/${userId}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  }, []);

  const sendUserOtp = useCallback(async (propertyId, phone) => {
    const res = await fetch(`${API}/api/properties/${propertyId}/users/send-otp`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `HTTP ${res.status}`);
    }
    return await res.json();
  }, []);

  const updateUserAccess = useCallback(async (userId, accessPermissions) => {
    const res = await fetch(`${API}/api/users/${userId}/access-permissions`, {
      method: 'PATCH',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ access_permissions: accessPermissions }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `HTTP ${res.status}`);
    }
    return await res.json();
  }, []);

  // Initial fetch + auto-sync every 30 seconds
  useEffect(() => {
    fetchProperties(false);
    intervalRef.current = setInterval(() => fetchProperties(false), 30000);
    return () => clearInterval(intervalRef.current);
  }, [fetchProperties]);

  const activeProperties = properties.filter(p => p.status === 'active');
  const totalCount = properties.length;
  const activeCount = activeProperties.length;

  return (
    <PropertiesContext.Provider value={{
      properties, activeProperties, loading, error, lastFetched, syncStatus,
      fetchProperties, fetchPropertyUsers, fetchPropertyDetail, toggleUserStatus, updatePropertyStatus,
      addUser, deleteUser, updatePropertyMapsLink, updateBookingVisibility, bulkUpdateBookingVisibility, resetUserPassword, notifyPasswordReset,
      sendUserOtp, updateUserAccess,
      totalCount, activeCount,
    }}>
      {children}
    </PropertiesContext.Provider>
  );
};

export default PropertiesContext;
